import React from 'react';
import { CreatorPlatformIcon } from '../ui/PlatformMultiSelect';
import Portrait from '../brand/Portrait';
import '../dashboard/dashboard.css';

/**
 * Live "this creator is being analyzed right now" tray shown above the
 * profile hero. Replaces the per-search-page pending-tile model — the
 * profile page is now the single global place a user sees in-flight
 * analyses, regardless of which page they triggered the analysis from.
 *
 * Source of truth comes from the backend ``/active-analysis`` endpoint
 * (see ``creator_active_jobs`` registry). One sub_job per platform; the
 * tray renders one row each and disappears when every row is terminal.
 *
 * On the desk it is a work tray (br-tray): a shallow manila folder bound on
 * the left, the place work-in-progress sits. Each platform is a ruled row
 * with one progress line; the status is typed, never spun — the page's bear
 * is the hero pin and it does not animate here.
 */

export type BannerSubJobStatus = 'pending' | 'processing' | 'complete' | 'error' | 'skipped' | 'unknown';

export interface BannerSubJob {
  platform: string;
  job_id: string;
  status?: string;
  is_complete?: boolean;
  progress?: {
    percentage_complete?: number;
    status_message?: string;
    videos_processed?: number;
    total_videos_found?: number;
    opportunities_found?: number;
    current_video_id?: string | null;
    current_video_thumbnail?: string | null;
    current_video?: string;
    [key: string]: unknown;
  } | null;
  error?: string | null;
  /** Stable category string from the backend (see scrape_errors.py).
   *  The banner branches on this to render category-specific copy +
   *  CTAs instead of dumping the raw exception. */
  error_category?: string | null;
  /** Follower-friendly one-line copy. Falls back to ``error`` when
   *  null (legacy code paths that haven't been migrated yet). */
  error_user_message?: string | null;
}

interface AnalysisProgressBannerProps {
  /** Pretty creator name to render at the top. Defaults to the handle. */
  displayName: string;
  /** Sub-jobs returned by ``getCreatorActiveAnalysis``. */
  subJobs: BannerSubJob[];
  /** Optional avatar to show alongside the headline. Falls back to the
   *  platform icon if not provided. */
  avatarUrl?: string | null;
  /** Fires once every sub-job is in a terminal state (complete/error/
   *  skipped). The parent can refetch the creator detail then. */
  onAllComplete?: () => void;
  /** Fires when at least one sub-job finishes. The parent can refetch the
   *  creator detail to keep the page in sync as data lands incrementally. */
  onAnyComplete?: (platform: string) => void;
}

const platformIconKey = (p: string): 'YouTube' | 'Instagram' | 'TikTok' | 'Twitch' => {
  const t = p.toLowerCase();
  if (t === 'youtube') return 'YouTube';
  if (t === 'instagram') return 'Instagram';
  if (t === 'tiktok') return 'TikTok';
  if (t === 'twitch') return 'Twitch';
  return 'YouTube';
};

const prettyPlatform = (p: string): string => {
  const t = p.toLowerCase();
  if (t === 'youtube') return 'YouTube';
  if (t === 'instagram') return 'Instagram';
  if (t === 'tiktok') return 'TikTok';
  if (t === 'twitch') return 'Twitch';
  return p;
};

const isTerminal = (sj: BannerSubJob): boolean => {
  if (sj.is_complete) return true;
  const s = (sj.status || '').toLowerCase();
  return s === 'complete' || s === 'error' || s === 'skipped';
};

const SubJobRow: React.FC<{ sub: BannerSubJob }> = ({ sub }) => {
  const status = (sub.status || '').toLowerCase();
  const terminal = isTerminal(sub);
  const isError = status === 'error';
  const isComplete = status === 'complete';
  const isSkipped = status === 'skipped';
  const pct = Math.max(0, Math.min(100, Number(sub.progress?.percentage_complete ?? 0)));
  const msg = sub.progress?.status_message || '';
  const thumb = (sub.progress?.current_video_thumbnail || '') as string;
  const currentTitle = (sub.progress?.current_video || '') as string;
  const oppsFound = Number(sub.progress?.opportunities_found ?? 0);
  const videosProcessed = Number(sub.progress?.videos_processed ?? 0);
  const totalVideos = Number(sub.progress?.total_videos_found ?? 0);

  return (
    <div className="flex items-start gap-3 py-2.5">
      <CreatorPlatformIcon
        platform={platformIconKey(sub.platform)}
        className="h-6 w-6 flex-shrink-0 mt-0.5"
        iconClassName="h-5 w-5"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-xs font-semibold text-text-primary">
            {prettyPlatform(sub.platform)}
          </span>
          <span className={`br-caption whitespace-nowrap ${isComplete ? 'br-ink-green font-semibold' : ''}`}>
            {isComplete
              ? (
                  oppsFound > 0
                    ? `${oppsFound} brand${oppsFound === 1 ? '' : 's'} found`
                    : 'Complete'
                )
              : isError
              ? 'Error'
              : isSkipped
              ? 'Not on platform'
              : totalVideos > 0
              ? `${videosProcessed} / ${totalVideos} · ${pct}%`
              : `${pct}%`}
          </span>
        </div>

        {!terminal && (
          <div className="br-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
            <span style={{ width: `${Math.max(pct, 5)}%` }} />
          </div>
        )}

        {!terminal && thumb && (
          <div className="flex items-center gap-2 mt-2">
            <img
              src={thumb}
              alt={currentTitle || 'Currently analyzing'}
              className="w-14 h-8 object-cover flex-shrink-0"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
            <span className="br-caption truncate">
              {currentTitle || msg || 'Checking…'}
            </span>
          </div>
        )}

        {!terminal && !thumb && msg && (
          <div className="br-caption truncate mt-1">{msg}</div>
        )}

        {isError && (
          <div className="br-caption br-ink-red truncate mt-1" title={sub.error || undefined}>
            {/* Prefer the follower-friendly copy from
                ``scrape_errors.user_message`` when available. Fall
                back to the raw exception text for jobs that pre-date
                the categorization layer. */}
            {sub.error_user_message || sub.error}
          </div>
        )}
        {isSkipped && (
          <div className="br-caption mt-1">
            No public profile detected
          </div>
        )}
      </div>
    </div>
  );
};

const AnalysisProgressBanner: React.FC<AnalysisProgressBannerProps> = ({
  displayName,
  subJobs,
  avatarUrl,
  onAllComplete,
  onAnyComplete,
}) => {
  // Track which sub-jobs have already fired their onAnyComplete so we
  // don't refetch on every poll once a platform finishes.
  const completedRef = React.useRef<Set<string>>(new Set());
  const allCompleteFiredRef = React.useRef(false);

  React.useEffect(() => {
    if (!subJobs || subJobs.length === 0) return;
    for (const sj of subJobs) {
      if (isTerminal(sj) && !completedRef.current.has(sj.job_id)) {
        completedRef.current.add(sj.job_id);
        onAnyComplete?.(sj.platform);
      }
    }
    const allDone = subJobs.every(isTerminal);
    if (allDone && !allCompleteFiredRef.current) {
      allCompleteFiredRef.current = true;
      // On a clean-success terminal state, hold the success layout
      // up for 5s so the user sees the payoff totals ("Found 7
      // sponsorships across 4 videos") before the banner unmounts.
      // Errors / partial-failures should fire immediately — users
      // need to see what broke without waiting.
      const cleanSuccess =
        subJobs.some((s) => (s.status || '').toLowerCase() === 'complete') &&
        !subJobs.some((s) => (s.status || '').toLowerCase() === 'error');
      const delay = cleanSuccess ? 5000 : 0;
      const t = window.setTimeout(() => { onAllComplete?.(); }, delay);
      return () => window.clearTimeout(t);
    }
  }, [subJobs, onAllComplete, onAnyComplete]);

  if (!subJobs || subJobs.length === 0) return null;

  const allDone = subJobs.every(isTerminal);
  const anyComplete = subJobs.some((s) => (s.status || '').toLowerCase() === 'complete');
  const anyError = subJobs.some((s) => (s.status || '').toLowerCase() === 'error');

  // Errors are grouped by category so the headline carries a single
  // actionable summary rather than echoing per-platform copy. When the
  // categories are uniform (most common case — every sub-job hit the
  // same upstream issue), we lean on the friendly copy directly.
  const errorSubs = subJobs.filter(s => (s.status || '').toLowerCase() === 'error');
  const errorCategories = new Set(
    errorSubs.map(s => (s.error_category || 'internal').toLowerCase()),
  );
  const primaryErrorCategory = errorCategories.size === 1
    ? Array.from(errorCategories)[0]
    : null;
  const primaryErrorMessage = errorSubs[0]?.error_user_message || errorSubs[0]?.error || null;

  // Totals for the success state — sum the real progress payload
  // fields (opportunities_found = persisted sponsorship count;
  // videos_processed = how many videos / posts we got through).
  const totalSponsorships = subJobs.reduce(
    (acc, s) => acc + (Number(s.progress?.opportunities_found) || 0),
    0,
  );
  const totalVideos = subJobs.reduce(
    (acc, s) => acc + (Number(s.progress?.videos_processed) || 0),
    0,
  );

  let headline: React.ReactNode;
  let tone: 'land' | 'sage' | 'honey' | undefined = 'land';
  if (allDone) {
    if (anyComplete && !anyError) {
      // 5-second celebratory state — show the actual numbers before
      // the banner unmounts. Beats the previous "Analysis complete —
      // refreshing profile…" placeholder that hid the payoff.
      tone = 'sage';
      headline = (
        <span className="br-ink-green inline-flex items-center text-xs font-semibold">
          {totalSponsorships > 0
            ? `Done — found ${totalSponsorships} sponsorship${totalSponsorships === 1 ? '' : 's'}${totalVideos > 0 ? ` across ${totalVideos} ${totalVideos === 1 ? 'item' : 'items'}` : ''}`
            : `Done — no new sponsorships this pass`}
        </span>
      );
    } else if (anyComplete && anyError) {
      tone = 'honey';
      headline = (
        <span className="br-ink-amber inline-flex items-center text-xs font-semibold">
          {primaryErrorCategory && primaryErrorMessage
            ? <>Partial success — {primaryErrorMessage}</>
            : 'Partial success — some platforms errored'}
        </span>
      );
    } else {
      // Total failure — surface the friendly category message
      // verbatim. Avoid "Analysis failed" when we know specifically
      // *why* (e.g. "We couldn't find that YouTube channel.").
      tone = undefined;
      headline = (
        <span className="br-ink-red inline-flex items-center text-xs font-semibold">
          {primaryErrorMessage || 'Analysis failed'}
        </span>
      );
    }
  } else {
    headline = (
      <span className="br-ink-blue inline-flex items-center text-xs font-semibold">
        Checking {subJobs.length} platform{subJobs.length === 1 ? '' : 's'}…
      </span>
    );
  }

  return (
    <div className={`br-tray${tone ? ' br-tray--tone' : ''} mb-4`} data-tone={tone} role="status" aria-live="polite">
      <div className="flex items-start gap-3 mb-2">
        {/* a creator's print sits on the tray; the owner's own check carries no face */}
        {avatarUrl && <Portrait name={displayName} src={avatarUrl} size="sm" />}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary truncate">{displayName}</p>
          <div className="mt-0.5">{headline}</div>
        </div>
      </div>

      <div className="br-ruled">
        {subJobs.map((sj) => (
          <SubJobRow key={`${sj.platform}:${sj.job_id}`} sub={sj} />
        ))}
      </div>

      {!allDone && (
        <p className="br-caption mt-2">
          Feel free to navigate away — analysis continues in the background and
          this page will refresh when it's done.
        </p>
      )}
    </div>
  );
};

export default AnalysisProgressBanner;
